
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { LocationGroupData } from '@/services/benchmarkDataService';

interface LocationGroupSectionProps {
  data: LocationGroupData;
  comparisonType: 'min' | 'avg';
}

const LocationGroupSection: React.FC<LocationGroupSectionProps> = ({ data, comparisonType }) => {
  const [showDetailedRecords, setShowDetailedRecords] = useState(false);

  const displayPrices = comparisonType === 'min' ? data.minPrices : data.avgPrices;
  const isChipChipSales = data.groupName === 'ChipChip Sales';

  // Process detailed data to create location-date matrix
  const processDetailedData = () => {
    const locationDateMatrix: { [locationKey: string]: { [date: string]: { price: number; origin?: string } | null } } = {};
    
    data.dates.forEach((date, dateIndex) => {
      const dateData = data.detailedData[dateIndex] || [];
      dateData.forEach(item => {
        // Create a unique key that includes origin for farm prices
        const locationKey = item.origin && data.groupName === 'Farm Prices' 
          ? `${item.location} (${item.origin})`
          : item.location;
        
        if (!locationDateMatrix[locationKey]) {
          locationDateMatrix[locationKey] = {};
        }
        locationDateMatrix[locationKey][date] = { 
          price: item.price, 
          origin: item.origin 
        };
      });
    });

    return locationDateMatrix;
  };

  const locationDateMatrix = processDetailedData();
  const uniqueLocations = Object.keys(locationDateMatrix);

  return (
    <Card className="mb-4 shadow-sm border-l-4 border-l-blue-500">
      <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
        <CardTitle className="text-lg font-bold text-blue-700">
          {data.groupName}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-0">
        <div className="overflow-hidden">
          {/* Summary Table - Always Visible */}
          <div className="border border-gray-200 bg-white">
            <Table className="border-collapse">
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                  <TableHead className="font-bold text-gray-800 border-r border-gray-200 px-4 py-3 text-left sticky left-0 bg-gray-50 min-w-[120px]">
                    Metric
                  </TableHead>
                  {data.dates.map((date) => (
                    <TableHead key={date} className="font-bold text-gray-800 border-r border-gray-200 px-4 py-3 text-center min-w-[120px] whitespace-nowrap">
                      {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isChipChipSales ? (
                  <TableRow className="hover:bg-green-25 border-b border-gray-200">
                    <TableCell className="font-semibold text-green-700 bg-green-50 border-r border-gray-200 px-4 py-3 sticky left-0">
                      Price
                    </TableCell>
                    {data.minPrices.map((price, index) => (
                      <TableCell key={index} className="text-center font-mono border-r border-gray-200 px-4 py-3 bg-white">
                        <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-md text-sm font-semibold">
                          {Math.round(price)}
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                ) : (
                  <>
                    <TableRow className="hover:bg-green-25 border-b border-gray-200">
                      <TableCell className="font-semibold text-green-700 bg-green-50 border-r border-gray-200 px-4 py-3 sticky left-0">
                        Min Price
                      </TableCell>
                      {data.minPrices.map((price, index) => (
                        <TableCell key={index} className="text-center font-mono border-r border-gray-200 px-4 py-3 bg-white">
                          <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-md text-sm font-semibold">
                            {Math.round(price)}
                          </span>
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className="hover:bg-blue-25 border-b border-gray-200">
                      <TableCell className="font-semibold text-blue-700 bg-blue-50 border-r border-gray-200 px-4 py-3 sticky left-0">
                        Avg Price
                      </TableCell>
                      {data.avgPrices.map((price, index) => (
                        <TableCell key={index} className="text-center font-mono border-r border-gray-200 px-4 py-3 bg-white">
                          <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-sm font-semibold">
                            {Math.round(price)}
                          </span>
                        </TableCell>
                      ))}
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Detailed Records Section - Collapsible */}
          <div className="p-6 space-y-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-gray-800 text-lg border-b-2 border-blue-200 pb-2">Detailed Records by Location</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetailedRecords(!showDetailedRecords)}
                className="flex items-center gap-2 hover:bg-blue-100"
              >
                {showDetailedRecords ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {showDetailedRecords ? 'Hide Details' : 'Show Details'}
              </Button>
            </div>

            {showDetailedRecords && uniqueLocations.length > 0 && (
              <Card className="border border-gray-200 shadow-sm">
                <CardContent className="p-0">
                  <div className="border border-gray-200 bg-white overflow-x-auto">
                    <Table className="border-collapse min-w-full">
                      <TableHeader>
                        <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                          <TableHead className="font-bold text-gray-800 border-r border-gray-200 px-4 py-3 text-left sticky left-0 bg-gray-50 min-w-[150px]">
                            {data.groupName === 'Farm Prices' ? 'Region (Origin)' : 'Location'}
                          </TableHead>
                          {data.dates.map((date) => (
                            <TableHead key={date} className="font-bold text-gray-800 border-r border-gray-200 px-4 py-3 text-center min-w-[120px] whitespace-nowrap">
                              {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uniqueLocations.map((location) => (
                          <TableRow key={location} className="hover:bg-gray-50 border-b border-gray-100">
                            <TableCell className="font-medium border-r border-gray-200 px-4 py-3 sticky left-0 bg-white">
                              {location}
                            </TableCell>
                            {data.dates.map((date) => {
                              const priceData = locationDateMatrix[location][date];
                              return (
                                <TableCell key={date} className="text-center font-mono border-r border-gray-200 px-4 py-3 bg-white">
                                  {priceData !== null && priceData !== undefined ? (
                                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-md text-sm font-semibold">
                                      {Math.round(priceData.price)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 text-sm">-</span>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LocationGroupSection;
